import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AIMarkdown } from "@/components/common/AIMarkdown";
import { streamPost, MODEL_LABEL, PROVIDERS } from "@/lib/sse";
import { cn } from "@/lib/utils";
import { Sparkles, Send, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROVIDER_KEY = "nyayyaksha_ai_provider";

// Streaming multi-turn chat backed by POST /api/ai/chat. `context` = {mode, matterId?, documentId?}
export function AIChat({ context, sessionId: initialSession, suggestions = [], placeholder = "Ask a question…", className, compact = false, onSession, initialMessage, renderActions }) {
  const [sessionId, setSessionId] = useState(initialSession || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState(() => localStorage.getItem(PROVIDER_KEY) || "auto");
  const abortRef = useRef(null);
  const endRef = useRef(null);
  const pickProvider = (p) => { setProvider(p); localStorage.setItem(PROVIDER_KEY, p); };

  useEffect(() => {
    if (!initialSession) { setMessages([]); setSessionId(null); return; }
    setSessionId(initialSession);
    axios.get(`${API}/ai/chat/${initialSession}`).then((r) => setMessages(r.data.map((m) => ({ role: m.role, content: m.content, model: m.model })))).catch(() => {});
  }, [initialSession]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Seeded query (e.g. "Research Requirement" from ProofGraph) — sent once.
  useEffect(() => {
    if (initialMessage && !initialSession) { const t = setTimeout(() => send(initialMessage), 300); return () => clearTimeout(t); }
    // eslint-disable-next-line
  }, [initialMessage]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "", model: null, streaming: true }]);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const patch = (fn) => setMessages((m) => { const copy = [...m]; copy[copy.length - 1] = fn(copy[copy.length - 1]); return copy; });
    try {
      const headers = await streamPost("/ai/chat", { ...context, sessionId, message: q, provider }, {
        signal: ctrl.signal,
        onMeta: (ev) => patch((a) => ({ ...a, model: ev.model })),
        onDelta: (d) => patch((a) => ({ ...a, content: a.content + d })),
        onError: (e) => patch((a) => ({ ...a, content: a.content || `⚠️ ${e}`, error: true })),
      });
      const sid = headers?.get?.("X-Session-Id");
      if (sid && !sessionId) { setSessionId(sid); onSession?.(sid); }
    } catch (e) {
      if (e.name !== "AbortError") patch((a) => ({ ...a, content: a.content || "⚠️ Connection lost.", error: true }));
    } finally {
      patch((a) => ({ ...a, streaming: false }));
      setBusy(false);
    }
  };

  return (
    <div className={cn("flex h-full flex-col", className)} data-testid="ai-chat">
      <div className="nyk-scroll flex-1 space-y-3 overflow-y-auto pr-1">
        {messages.length === 0 && (
          <div className={cn("rounded-xl border border-dashed border-line p-4 text-center", compact ? "text-xs" : "text-sm")}>
            <Sparkles className="mx-auto mb-1 h-5 w-5 text-copper" />
            <div className="text-ink-2">Ask anything. Answers stream live and cite the records they rely on.</div>
            {suggestions.length > 0 && <div className="mt-3 flex flex-wrap justify-center gap-1.5">{suggestions.map((s) => <button key={s} onClick={() => send(s)} className="rounded-full border border-line px-2.5 py-1 text-xs text-ink-2 hover:border-copper/40" data-testid="ai-suggestion">{s}</button>)}</div>}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")} data-testid={`ai-msg-${m.role}`}>
            {m.role === "user" ? (
              <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-navy px-3.5 py-2 text-sm text-white">{m.content}</div>
            ) : (
              <div className={cn("max-w-[95%] rounded-2xl rounded-bl-sm border border-line bg-card px-4 py-3", m.error && "border-danger/40")}>
                {m.model && <div className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wide text-ink-muted" data-testid="ai-model-badge"><Sparkles className="h-3 w-3 text-copper" />{MODEL_LABEL[m.model] || m.model}{m.streaming && <span className="ml-1 animate-pulse text-copper">● thinking</span>}</div>}
                {m.content ? <AIMarkdown text={m.content} /> : <div className="flex gap-1 py-1"><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-copper" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-copper [animation-delay:120ms]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-copper [animation-delay:240ms]" /></div>}
                {m.content && !m.streaming && !m.error && renderActions && <div className="mt-2 flex flex-wrap gap-1 border-t border-line pt-1.5" data-testid="ai-msg-actions">{renderActions(m, sessionId, () => { const s = window.getSelection()?.toString().trim(); return s && m.content.includes(s.slice(0, 40)) ? s : null; })}</div>}
              </div>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="mt-3 flex items-end gap-2">
        <div className="flex flex-col gap-1.5">
          <div className="flex rounded-md border border-line bg-surface p-0.5" data-testid="ai-provider-switch" role="radiogroup" aria-label="AI provider">
            {PROVIDERS.map((p) => (
              <button key={p.id} role="radio" aria-checked={provider === p.id} onClick={() => pickProvider(p.id)} disabled={busy}
                className={cn("rounded px-2 py-1 text-[11px] font-medium", provider === p.id ? "bg-navy text-white" : "text-ink-2 hover:text-ink")} data-testid={`ai-provider-${p.id}`}>{p.label}</button>
            ))}
          </div>
        </div>
        <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={compact ? 2 : 3} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="ai-chat-input" />
        {busy ? (
          <Button variant="outline" onClick={() => abortRef.current?.abort()} data-testid="ai-chat-stop"><Square className="h-4 w-4" /></Button>
        ) : (
          <Button onClick={() => send()} disabled={!input.trim()} data-testid="ai-chat-send"><Send className="h-4 w-4" /></Button>
        )}
      </div>
    </div>
  );
}
